'use client'

import Link from 'next/link'
import Script from 'next/script'
import { useRef, useState, useTransition, type FormEvent, type ReactNode } from 'react'
import { Icon } from './Icon'
import { submitApplication, submitContact } from '@/lib/forms/actions'
import {
  BUDGETS,
  CV_MAX_BYTES,
  EXPERTISE_TO_PROJECT,
  PROJECT_TYPES,
  applicationSchema,
  contactSchema,
  quoteSchema,
  toFieldErrors,
  type FieldErrors,
  type FormState,
} from '@/lib/forms/schemas'

const TURNSTILE = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

function Field({
  id,
  label,
  optional,
  hint,
  error,
  children,
}: {
  id: string
  label: string
  optional?: boolean
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <div className="field" data-invalid={error ? 'true' : undefined}>
      <label htmlFor={id}>
        {label} {optional ? <span className="opt">(facultatif)</span> : <span aria-hidden="true" style={{ color: 'var(--ex-danger)' }}>*</span>}
      </label>
      {children}
      {hint ? (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={`${id}-error`} role="alert">
          <Icon name="alert" width={16} height={16} /> {error}
        </span>
      ) : null}
    </div>
  )
}

const describedBy = (id: string, errors: FieldErrors, hint?: boolean) =>
  [errors[id] ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ') || undefined

function Status({ state }: { state: FormState }) {
  if (state.status === 'idle') return null
  const ok = state.status === 'success'
  return (
    <div className={`form-status ${ok ? 'form-status--success' : 'form-status--error'}`} role={ok ? 'status' : 'alert'} tabIndex={-1}>
      <Icon name={ok ? 'check' : 'alert'} />
      <div>
        <strong>{ok ? 'Merci, c’est bien reçu.' : 'Envoi non effectué'}</strong>
        {state.message}
      </div>
    </div>
  )
}

function Antispam({ token }: { token: string }) {
  return (
    <>
      <div className="hp" aria-hidden="true">
        <label htmlFor="website">Ne pas remplir ce champ</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <input type="hidden" name="_t" value={token} />
      {TURNSTILE ? (
        <>
          <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />
          <div className="cf-turnstile" data-sitekey={TURNSTILE} data-language="fr" />
        </>
      ) : null}
    </>
  )
}

function Consent({ error, children }: { error?: string; children: ReactNode }) {
  return (
    <div className="field" data-invalid={error ? 'true' : undefined}>
      <label className="check" style={{ fontWeight: 400, color: 'var(--ex-ink)' }}>
        <input type="checkbox" name="consent" required aria-describedby={error ? 'consent-error' : undefined} aria-invalid={Boolean(error)} />
        <span>{children}</span>
      </label>
      {error ? (
        <span className="field__error" id="consent-error" role="alert">
          <Icon name="alert" width={16} height={16} /> {error}
        </span>
      ) : null}
    </div>
  )
}

function Submit({ pending, label }: { pending: boolean; label: string }) {
  return (
    <div>
      <button type="submit" className="btn" disabled={pending} aria-disabled={pending}>
        {pending ? (
          <>
            <span className="spinner" aria-hidden="true" /> Envoi en cours…
          </>
        ) : (
          <>
            {label} <Icon name="arrow" className="arrow" />
          </>
        )}
      </button>
      <p className="xs muted" style={{ marginTop: 14 }}>
        <span aria-hidden="true" style={{ color: 'var(--ex-danger)' }}>*</span> Champs obligatoires.
      </p>
    </div>
  )
}

function useFormSubmit(
  validate: (fd: FormData) => FieldErrors,
  action: (fd: FormData) => Promise<FormState>,
) {
  const [state, setState] = useState<FormState>({ status: 'idle' })
  const [errors, setErrors] = useState<FieldErrors>({})
  const [pending, start] = useTransition()
  const ref = useRef<HTMLFormElement>(null)

  const focusFirst = (errs: FieldErrors) => {
    const first = Object.keys(errs)[0]
    if (first) ref.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const errs = validate(fd)
    setErrors(errs)
    if (Object.keys(errs).length) {
      setState({ status: 'error', message: 'Certains champs sont à corriger.' })
      focusFirst(errs)
      return
    }
    start(async () => {
      try {
        const res = await action(fd)
        setState(res)
        setErrors(res.fieldErrors || {})
        if (res.status === 'success') {
          ref.current?.reset()
          ref.current?.querySelector<HTMLElement>('.form-status')?.focus()
        } else if (res.fieldErrors) {
          focusFirst(res.fieldErrors)
        }
      } catch {
        setState({
          status: 'error',
          message: 'La connexion au serveur a échoué. Vérifiez votre connexion internet puis réessayez.',
        })
      }
      requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('.form-status')?.focus())
    })
  }
  return { state, errors, pending, ref, onSubmit }
}

const pick = (fd: FormData, keys: string[]) => Object.fromEntries(keys.map((k) => [k, (fd.get(k) ?? '').toString()]))

export function ContactForm({ token, kind, defaultProject }: { token: string; kind: 'contact' | 'devis'; defaultProject?: string }) {
  const { state, errors, pending, ref, onSubmit } = useFormSubmit((fd) => {
    const r =
      kind === 'devis'
        ? quoteSchema.safeParse(pick(fd, ['name', 'email', 'phone', 'organisation', 'role', 'projectType', 'budget', 'deadline', 'message', 'consent']))
        : contactSchema.safeParse(pick(fd, ['name', 'email', 'phone', 'organisation', 'subject', 'message', 'consent']))
    return r.success ? {} : toFieldErrors(r.error.issues)
  }, submitContact)

  const projectDefault = (defaultProject && EXPERTISE_TO_PROJECT[defaultProject]) || ''

  return (
    <form ref={ref} className="form" onSubmit={onSubmit} noValidate aria-describedby="form-legal">
      <input type="hidden" name="kind" value={kind} />
      <div className="form-row">
        <Field id="name" label="Nom et prénom" error={errors.name}>
          <input id="name" name="name" className="input" autoComplete="name" required maxLength={120} aria-invalid={Boolean(errors.name)} aria-describedby={describedBy('name', errors)} />
        </Field>
        <Field id="email" label="E-mail professionnel" error={errors.email}>
          <input id="email" name="email" type="email" className="input" autoComplete="email" required maxLength={160} aria-invalid={Boolean(errors.email)} aria-describedby={describedBy('email', errors)} />
        </Field>
      </div>
      <div className="form-row">
        <Field id="phone" label="Téléphone" optional error={errors.phone}>
          <input id="phone" name="phone" type="tel" className="input" autoComplete="tel" maxLength={30} aria-invalid={Boolean(errors.phone)} aria-describedby={describedBy('phone', errors)} />
        </Field>
        <Field id="organisation" label="Organisation" optional={kind === 'contact'} error={errors.organisation}>
          <input id="organisation" name="organisation" className="input" autoComplete="organization" required={kind === 'devis'} maxLength={160} aria-invalid={Boolean(errors.organisation)} aria-describedby={describedBy('organisation', errors)} />
        </Field>
      </div>

      {kind === 'devis' ? (
        <>
          <div className="form-row">
            <Field id="role" label="Fonction" optional error={errors.role}>
              <input id="role" name="role" className="input" autoComplete="organization-title" maxLength={120} />
            </Field>
            <Field id="projectType" label="Type de projet" error={errors.projectType}>
              <select id="projectType" name="projectType" className="select" required defaultValue={projectDefault} aria-invalid={Boolean(errors.projectType)} aria-describedby={describedBy('projectType', errors)}>
                <option value="" disabled>
                  Sélectionnez…
                </option>
                {PROJECT_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="form-row">
            <Field id="budget" label="Budget indicatif" optional>
              <select id="budget" name="budget" className="select" defaultValue="">
                <option value="">Je préfère en discuter</option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="deadline" label="Échéance souhaitée" optional hint="Ex. : mise en ligne avant juin, dans 3 mois…">
              <input id="deadline" name="deadline" className="input" maxLength={120} aria-describedby="deadline-hint" />
            </Field>
          </div>
          <Field id="message" label="Description du besoin" error={errors.message} hint="Contexte, objectifs, utilisateurs concernés, existant, contraintes particulières…">
            <textarea id="message" name="message" className="textarea" required minLength={30} maxLength={8000} aria-invalid={Boolean(errors.message)} aria-describedby={describedBy('message', errors, true)} />
          </Field>
        </>
      ) : (
        <>
          <Field id="subject" label="Objet" error={errors.subject}>
            <input id="subject" name="subject" className="input" required maxLength={160} aria-invalid={Boolean(errors.subject)} aria-describedby={describedBy('subject', errors)} />
          </Field>
          <Field id="message" label="Message" error={errors.message}>
            <textarea id="message" name="message" className="textarea" required minLength={20} maxLength={5000} aria-invalid={Boolean(errors.message)} aria-describedby={describedBy('message', errors)} />
          </Field>
        </>
      )}

      <Antispam token={token} />
      <Consent error={errors.consent}>
        J’accepte qu’EXELEC utilise ces informations pour répondre à ma demande. Elles ne sont ni cédées ni utilisées à d’autres fins.
        Voir la <Link href="/confidentialite">politique de confidentialité</Link>.
      </Consent>
      <Status state={state} />
      <Submit pending={pending} label={kind === 'devis' ? 'Envoyer ma demande de devis' : 'Envoyer le message'} />
      <p id="form-legal" className="sr-only">
        Formulaire de {kind === 'devis' ? 'demande de devis' : 'contact'} EXELEC.
      </p>
    </form>
  )
}

export function ApplicationForm({ token, jobId, jobTitle }: { token: string; jobId?: number | string; jobTitle?: string }) {
  const { state, errors, pending, ref, onSubmit } = useFormSubmit((fd) => {
    const r = applicationSchema.safeParse(pick(fd, ['name', 'email', 'phone', 'position', 'linkedin', 'message', 'jobId', 'consent']))
    const errs = r.success ? {} : toFieldErrors(r.error.issues)
    const f = fd.get('cv')
    if (!(f instanceof File) || f.size === 0) errs.cv = 'Joignez votre CV (PDF ou Word).'
    else if (f.size > CV_MAX_BYTES) errs.cv = 'Le fichier dépasse 5 Mo.'
    else if (!/\.(pdf|docx?)$/i.test(f.name)) errs.cv = 'Format non accepté : PDF, DOC ou DOCX uniquement.'
    return errs
  }, submitApplication)

  return (
    <form ref={ref} className="form" onSubmit={onSubmit} noValidate encType="multipart/form-data">
      <input type="hidden" name="jobId" value={jobId ? String(jobId) : ''} />
      <div className="form-row">
        <Field id="name" label="Nom et prénom" error={errors.name}>
          <input id="name" name="name" className="input" autoComplete="name" required maxLength={120} aria-invalid={Boolean(errors.name)} aria-describedby={describedBy('name', errors)} />
        </Field>
        <Field id="email" label="E-mail" error={errors.email}>
          <input id="email" name="email" type="email" className="input" autoComplete="email" required maxLength={160} aria-invalid={Boolean(errors.email)} aria-describedby={describedBy('email', errors)} />
        </Field>
      </div>
      <div className="form-row">
        <Field id="phone" label="Téléphone" optional error={errors.phone}>
          <input id="phone" name="phone" type="tel" className="input" autoComplete="tel" maxLength={30} aria-invalid={Boolean(errors.phone)} aria-describedby={describedBy('phone', errors)} />
        </Field>
        {jobTitle ? (
          <Field id="position" label="Poste visé" optional>
            <input id="position" name="position" className="input" defaultValue={jobTitle} readOnly />
          </Field>
        ) : (
          <Field id="position" label="Poste ou domaine souhaité" optional error={errors.position}>
            <input id="position" name="position" className="input" maxLength={160} placeholder="Ex. : développeur web, chef de projet…" />
          </Field>
        )}
      </div>
      <Field id="linkedin" label="Profil en ligne (LinkedIn, portfolio, GitHub…)" optional error={errors.linkedin}>
        <input id="linkedin" name="linkedin" type="url" className="input" inputMode="url" placeholder="https://" maxLength={300} aria-invalid={Boolean(errors.linkedin)} aria-describedby={describedBy('linkedin', errors)} />
      </Field>
      <Field id="message" label="Votre message" error={errors.message} hint="Votre parcours, vos compétences et ce qui vous attire chez EXELEC.">
        <textarea id="message" name="message" className="textarea" required minLength={30} maxLength={5000} aria-invalid={Boolean(errors.message)} aria-describedby={describedBy('message', errors, true)} />
      </Field>
      <Field id="cv" label="CV" error={errors.cv} hint="PDF, DOC ou DOCX — 5 Mo maximum.">
        <div className="file">
          <Icon name="upload" width={22} height={22} style={{ color: 'var(--ex-cobalt)', flex: 'none' }} />
          <input
            id="cv"
            name="cv"
            type="file"
            required
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            aria-invalid={Boolean(errors.cv)}
            aria-describedby={describedBy('cv', errors, true)}
          />
        </div>
      </Field>
      <Antispam token={token} />
      <Consent error={errors.consent}>
        J’accepte qu’EXELEC conserve ma candidature et mon CV afin de l’étudier, pour une durée maximale de deux ans. Voir la{' '}
        <Link href="/confidentialite">politique de confidentialité</Link>.
      </Consent>
      <Status state={state} />
      <Submit pending={pending} label="Envoyer ma candidature" />
    </form>
  )
}
